import { FieldValues, Path, PathValue, useFormContext } from 'react-hook-form';
import { FormComponentProps } from '../types';
import { Title } from './Title';

interface FormTagGroupProps<T> extends FormComponentProps<T> {
  title: string;
  hideTitle?: boolean;
  classname?: string;
  options: { title: string; value: PathValue<T, Path<T>>; turnsOff?: string[] }[];
}

const selectedClass = 'bg-solid-bold-green text-solid-deep-blue border-solid-bold-green hover:text-solid-deep-blue jbp-tag-selected';
const unselectedClass = 'text-interactive-coral border-interactive-coral hover:border-solid-bold-green jbp-tag-unselected';

export const FormTagGroup = <T extends FieldValues>(props: FormTagGroupProps<T>) => {
  const { register, watch, setValue } = useFormContext<T>();
  const inputProps = register<Path<T>>(props.name);
  const fieldValue = watch(props.name);

  const onTagClick = (option: { title: string; value: PathValue<T, Path<T>>; turnsOff?: string[] }) => {
    if (props.readonly) return;

    if (!fieldValue) {
      setValue(props.name, [option.value] as any, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
      return;
    }

    if (fieldValue.includes(option.value)) {
      setValue(
        props.name,
        fieldValue.filter((v: PathValue<T, Path<T>>) => v !== option.value),
        { shouldDirty: true, shouldTouch: true, shouldValidate: true }
      );
      return;
    }

    let newValue: PathValue<T, Path<T>> = [...fieldValue, option.value] as any;

    if (option.turnsOff) {
      newValue = newValue.filter((v: PathValue<T, Path<T>>) => !option.turnsOff?.includes(v as unknown as string));
    }
    setValue(props.name, newValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  };

  const renderTag = (option: { title: string; value: PathValue<T, Path<T>>; turnsOff?: string[] }) => {
    const selected = fieldValue && fieldValue.includes(option.value);
    return (
      <button
        key={option.value}
        onClick={() => onTagClick(option)}
        className={`inline-block cursor-pointer p-2 pr-5 pl-5 m-1 ml-0 mr-2 font-bold text-sm rounded-3xl border-2 hover:text-solid-bold-green ${selected ? selectedClass : unselectedClass}`}
      >
        <span className="inline-block">{option.title}</span>
      </button>
    );
  };

  return (
    <div className={`mb-5 ${props.classname}`} {...inputProps}>
      <Title text={props.title} className={`mt-7 mb-5 ${props.hideTitle == true ? 'hidden' : ''}`} />
      {props.options.map(renderTag)}
    </div>
  );
};
